/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type CheckboxProps as CheckboxBaseProps, Checkbox as CheckboxCore, type CheckboxRenderProps as CheckboxRenderPropsBase, type ContextValue, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { Icon, IconContext } from '../icon/icon.component';
import { Label, type LabelProps } from '../label/label.component';

interface CheckboxProps extends Omit<CheckboxBaseProps, 'style' | 'className'> {
  /** The size of the checkbox. (Default: 20) */
  size?: keyof typeof sizeVariants;
  style?: StyleOrFunction<CheckboxRenderPropsBase> | {
    base?: StyleOrFunction<CheckboxRenderPropsBase>;
    icon?: StyleOrFunction<CheckboxRenderPropsBase>;
    label?: StyleOrFunction<CheckboxRenderPropsBase>;
  };
}

const CheckboxContext = createContext<ContextValue<CheckboxProps, HTMLLabelElement>>({});

const Checkbox = forwardRef((props: CheckboxProps, ref: ForwardedRef<HTMLLabelElement>) => {
  [props, ref] = useContextProps(props, ref, CheckboxContext);

  const { style, size = 20, children, ...coreProps } = props;

  return (
    <CheckboxCore
      ref={ref}
      {...applyStylesWithRenderProps<CheckboxRenderPropsBase>((state) => stylex.props(styles.base, sizeVariants[size], resolveComponentStyle(style, state, 'base')))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        return (
          <IconContext.Provider value={{
            size: ICON_SIZES[size],
            color: (
              (renderProps.isDisabled && (renderProps.isSelected ? 'disabled' : 'inherit')) ||
              (renderProps.isInvalid && (renderProps.isSelected ? 'critical' : 'criticalSilent')) ||
              renderProps.isSelected && 'white' ||
              'inherit'
            ),
            style: [
              iconStyles.base,
              iconSizeVariants[size],
              renderProps.isHovered && iconStyles.hovered,
              renderProps.isSelected && iconStyles.selected,
              renderProps.isInvalid && iconStyles.invalid,
              renderProps.isDisabled && iconStyles.disabled,
              renderProps.isFocusVisible && iconStyles.focused,
              resolveComponentStyle(style, renderProps, 'icon')
            ]
          }}>
            <Icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true">
                {renderProps.isIndeterminate
                  ? <path d="M20.4001 12L3.6001 12" strokeLinecap="round" strokeWidth="4" strokeLinejoin="round" />
                  : <path d="M3.59998 12L9.53967 17.7463L21 6.6" strokeLinecap="round" strokeWidth="4" strokeLinejoin="round" />}
              </svg>
            </Icon>
            {evaluatedChildren && (
              <Label
                size={LABEL_SIZES[size]}
                style={[labelStyles.base, resolveComponentStyle(style, renderProps, 'label')]}
                color={(
                  (renderProps.isDisabled && 'disabled') ||
                  (renderProps.isInvalid && 'critical') ||
                  (renderProps.isSelected || renderProps.isHovered) && 'primary' ||
                  'tertiary'
                )}>
                {evaluatedChildren}
              </Label>
            )}
          </IconContext.Provider>
        );
      }}
    </CheckboxCore>
  );
});

const LABEL_SIZES: Record<keyof typeof sizeVariants, LabelProps['size']> = {
  16: 14,
  20: 16,
  24: 18,
};

const ICON_SIZES: Record<keyof typeof sizeVariants, number> = {
  16: 16,
  20: 18,
  24: 20,
};

const styles = stylex.create({
  base: {
    boxSizing: 'border-box',
    color: $.textTertiary,
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'relative',
    userSelect: 'none',
    width: 'fit-content',
  },
});

const sizeVariants = stylex.create({
  16: { gap: 8 },
  20: { gap: 12 },
  24: { gap: 16 },
});

const iconStyles = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    borderColor: $.border10,
    borderStyle: 'solid',
    boxSizing: 'border-box',
    transitionDuration: '200ms',
    transitionProperty: 'background-color, border-color, outline-offset',
    transitionTimingFunction: 'ease-in-out',
  },
  disabled: {
    backgroundColor: $.surfaceBGPrimary,
    borderColor: $.border08,
  },
  hovered: { borderColor: $.borderContrast },
  selected: {
    backgroundColor: $.surfaceHighlight,
    borderColor: $.border08,
  },
  invalid: {
    backgroundColor: $.surfaceCriticalSilent,
    borderColor: $.borderCritical,
  },
  focused: {
    outlineColor: $.borderInteractive,
    outlineOffset: '2px',
    outlineStyle: 'solid',
    outlineWidth: '2px',
  }
});

const iconSizeVariants = stylex.create({
  16: {
    borderRadius: 4,
    borderWidth: 1,
    boxShadow: $.shadowBottom010Silent,
    height: 16,
    padding: 2,
    width: 16,
  },
  20: {
    borderRadius: 5,
    borderWidth: 1.5,
    boxShadow: $.shadowBottom030Silent,
    height: 20,
    padding: 2,
    width: 20,
  },
  24: {
    borderRadius: 6,
    borderWidth: 1.5,
    boxShadow: $.shadowBottom030Silent,
    height: 24,
    padding: 4,
    width: 24,
  }
});

const labelStyles = stylex.create({
  base: {
    transitionDuration: '200ms',
    transitionProperty: 'color',
  }
});

export { Checkbox, CheckboxContext, type CheckboxProps };
